import {
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  ClipboardPen,
  FileUp,
  Lightbulb,
  LockKeyhole,
  MessageCircle,
  Play,
  Send,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BrandedVideoPlayer } from "@/components/BrandedVideoPlayer";
import { LessonBlockRenderer } from "@/components/LessonBlockRenderer";
import { LessonCustomForm } from "@/components/LessonCustomForm";
import { StudentContactModal } from "@/components/StudentContactModal";
import { isAppearanceVisible } from "@/lib/lessonVisibility";

const ctaIcons = { external: ArrowUpRight, whatsapp: MessageCircle, telegram: Send, download: FileUp, calendar: CalendarDays, play: Play };

function CourseCtaIcon({ name }) {
  const Icon = ctaIcons[name];
  return Icon ? <Icon aria-hidden="true"/> : null;
}

export default function CoursePlayerV2() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [lessonId, setLessonId] = useState(null);
  const [outline, setOutline] = useState(false);
  const [content, setContent] = useState("");
  const [answers, setAnswers] = useState([]);
  const [fileId, setFileId] = useState(null);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [videoSeconds, setVideoSeconds] = useState(0);
  const [lessonOpenedAt, setLessonOpenedAt] = useState(null);
  const [appearanceTick, setAppearanceTick] = useState(() => Date.now());
  const progressRef = useRef({
    lessonId: null,
    seconds: 0,
    savedAt: 0,
    completionSent: false,
    unlockSent: false,
  });
  const load = useCallback(
    () =>
      api.get(`/courses/${courseId}`).then(({ data }) => {
        setCourse(data);
        setLessonId(
          (current) =>
            current ||
            searchParams.get("lesson") ||
            data.enrollment?.resume_lesson_id ||
            data.modules?.[0]?.lessons?.[0]?.id,
        );
      }),
    [courseId, searchParams],
  );
  useEffect(() => {
    load().catch(() => navigate("/kursy"));
  }, [load, navigate]);
  const lessons = useMemo(
    () =>
      course?.modules?.flatMap((m) =>
        m.lessons.map((l) => ({ ...l, moduleTitle: m.title })),
      ) || [],
    [course],
  );
  const index = lessons.findIndex((l) => l.id === lessonId);
  const lesson = lessons[index];
  const completed = useMemo(
    () => course?.enrollment?.completed_lessons || [],
    [course?.enrollment?.completed_lessons],
  );
  const locked = course?.locked_lessons || [];
  const lessonLocked = Boolean(lesson && locked.includes(lesson.id));
  useEffect(() => {
    if (!lesson?.id || lessonLocked) return;
    let active = true;
    api.post(`/courses/${courseId}/lessons/${lesson.id}/open`).then(({ data }) => {
      if (active) setLessonOpenedAt(data.opened_at);
    }).catch(() => {});
    return () => { active = false; };
  }, [courseId, lesson?.id, lessonLocked]);
  useEffect(() => {
    const hasTimed = (lesson?.content_blocks || []).some((block) => ["lesson_open", "date"].includes(block.appearance?.mode)) || (lesson?.custom_buttons || []).some((button) => ["lesson_open", "date"].includes(button.appearance?.mode));
    if (!hasTimed) return undefined;
    const timer = window.setInterval(() => setAppearanceTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [lesson?.content_blocks, lesson?.custom_buttons]);
  const choose = (id) => {
    if (locked.includes(id))
      return toast.error("Ta lekcja jest jeszcze zablokowana");
    setLessonId(id);
    setOutline(false);
    setContent("");
    setAnswers([]);
    setFileId(null);
    setVideoSeconds(0);
    setLessonOpenedAt(null);
    progressRef.current = {
      lessonId: id,
      seconds: 0,
      savedAt: 0,
      completionSent: false,
      unlockSent: false,
    };
  };
  const saveProgress = useCallback(
    (seconds, duration) => {
      if (
        !course?.enrollment ||
        !lesson ||
        !Number.isFinite(seconds) ||
        !Number.isFinite(duration) ||
        duration <= 0
      )
        return;
      setVideoSeconds(seconds);
      if (progressRef.current.lessonId !== lesson.id)
        progressRef.current = {
          lessonId: lesson.id,
          seconds: 0,
          savedAt: 0,
          completionSent: false,
          unlockSent: false,
        };
      const state = progressRef.current;
      const percent = (seconds / duration) * 100;
      const savedPercent = Number(
        course.enrollment.lesson_positions?.[lesson.id]?.percent || 0,
      );
      const now = Date.now();
      const reachingUnlock =
        percent >= 85 && savedPercent < 85 && !state.unlockSent;
      const reachingCompletion =
        percent >= 90 &&
        !completed.includes(lesson.id) &&
        !state.completionSent;
      if (
        !reachingUnlock &&
        !reachingCompletion &&
        now - state.savedAt < 8000 &&
        Math.abs(seconds - state.seconds) < 10
      )
        return;
      state.seconds = seconds;
      state.savedAt = now;
      if (reachingUnlock) state.unlockSent = true;
      if (reachingCompletion) state.completionSent = true;
      api
        .post(`/courses/${courseId}/lessons/${lesson.id}/progress`, {
          seconds,
          duration,
        })
        .then(({ data }) => {
          setCourse((currentCourse) =>
            currentCourse
              ? {
                  ...currentCourse,
                  enrollment: {
                    ...currentCourse.enrollment,
                    completed_lessons: data.completed_lessons,
                    progress: data.progress,
                    lesson_positions: {
                      ...(currentCourse.enrollment?.lesson_positions || {}),
                      [lesson.id]: data.position,
                    },
                    resume_lesson_id: lesson.id,
                    resume_seconds: data.position.seconds,
                    resume_percent: data.position.percent,
                  },
                }
              : currentCourse,
          );
          if (data.auto_completed)
            toast.success("Lekcja ukończona automatycznie");
          if (data.unlock_triggered || data.auto_completed) load();
        })
        .catch(() => {});
    },
    [completed, course?.enrollment, courseId, lesson, load],
  );
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/files", form);
    setFileId(data.id);
    toast.success("Plik gotowy do wysłania");
  };
  const submit = async () => {
    try {
      const { data } = await api.post("/homework", {
        course_id: courseId,
        lesson_id: lesson.id,
        content,
        file_id: fileId,
        answers,
      });
      await load();
      toast.success(
        lesson.assignment_type === "quiz"
          ? data.status === "approved"
            ? `Test zaliczony: ${data.grade}%`
            : `Wynik ${data.grade}% — spróbuj ponownie`
          : "Praca wysłana do kuratora",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uzupełnij zadanie");
    }
  };
  const trackButton = (targetId, label) => api.post("/analytics/events", { event_type: "button_click", course_id: courseId, lesson_id: lesson?.id, target_id: targetId, label, metadata: {} }).catch(() => {});
  if (!course || !lesson)
    return (
      <div className="page loading" data-testid="course-player-loading">
        Przygotowujemy lekcję…
      </div>
    );
  const savedPosition =
    course.enrollment?.lesson_positions?.[lesson.id]?.seconds ||
    (course.enrollment?.resume_lesson_id === lesson.id
      ? course.enrollment?.resume_seconds
      : 0) ||
    0;
  const accessibleLesson =
    lessons.slice(0, Math.max(index, 0)).reverse().find((item) => !locked.includes(item.id)) ||
    lessons.find((item) => !locked.includes(item.id));
  const appearanceContext = { lessonOpenedAt, videoSeconds };
  const visibleButtons = (lesson.custom_buttons || []).filter((button) => isAppearanceVisible(button.appearance, appearanceContext, appearanceTick));
  return (
    <div
      className={`player-v2 ${outline ? "outline-open" : ""}`}
      data-testid="course-player-page"
    >
      <button
        className="outline-toggle"
        onClick={() => setOutline(!outline)}
        aria-expanded={outline}
        data-testid="lesson-outline-toggle"
      >
        <span>PROGRAM</span>
        {outline ? <ChevronDown /> : <ChevronUp />}
      </button>
      <aside
        className={`player-outline-glass ${outline ? "open" : ""}`}
        data-testid="course-outline"
      >
        <button
          className="outline-close"
          onClick={() => setOutline(false)}
          data-testid="close-outline-button"
        >
          <X />
        </button>
        <button
          className="back-link"
          onClick={() => navigate("/kursy")}
          data-testid="back-to-courses-button"
        >
          <ChevronLeft /> MOJE KURSY
        </button>
        <h2 data-testid="player-course-title">{course.title}</h2>
        <div className="overall-v2">
          <span>TWÓJ POSTĘP</span>
          <strong>{course.enrollment?.progress || 0}%</strong>
          <i>
            <b style={{ width: `${course.enrollment?.progress || 0}%` }} />
          </i>
        </div>
        {course.modules.map((module, mi) => (
          <div className="outline-module-v2" key={module.id}>
            <span>
              {course.show_module_numbers !== false &&
                `${module.number_label || mi + 1}. `}
              {module.title}
            </span>
            {module.lessons.map((item, li) => {
              const isLocked = locked.includes(item.id);
              const position = course.enrollment?.lesson_positions?.[item.id];
              const watchedPercent =
                position?.percent >= 90
                  ? 100
                  : Math.round(position?.percent || 0);
              return (
                <button
                  className={item.id === lesson.id ? "active" : ""}
                  onClick={() => choose(item.id)}
                  key={item.id}
                  data-testid={`lesson-nav-${item.id}`}
                >
                  {isLocked ? (
                    <LockKeyhole />
                  ) : completed.includes(item.id) ? (
                    <Check />
                  ) : (
                    <Circle />
                  )}
                  <div>
                    <strong>
                      {course.show_lesson_numbers !== false &&
                        `${item.number_label || `${module.number_label || mi + 1}.${li + 1}`} `}
                      {item.title}
                    </strong>
                    <small>
                      {isLocked
                        ? "Spełnij warunek"
                        : completed.includes(item.id)
                          ? "Ukończona"
                          : watchedPercent
                            ? `${watchedPercent}% obejrzano`
                            : item.duration}
                    </small>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </aside>
      {lessonLocked ? (
        <main className="lesson-v2 locked-lesson-screen" data-testid="locked-lesson-screen">
          <section className="locked-lesson-panel">
            <span className="locked-lesson-icon"><LockKeyhole aria-hidden="true" /></span>
            <small>LEKCJA ZABLOKOWANA</small>
            <h1>Najpierw ukończ poprzedni etap</h1>
            <p>Treść, wideo i materiały tej lekcji pojawią się automatycznie po spełnieniu warunku dostępu.</p>
            {accessibleLesson ? <button className="gold-button" onClick={() => choose(accessibleLesson.id)} data-testid="return-to-open-lesson-button"><ChevronLeft /> WRÓĆ DO DOSTĘPNEJ LEKCJI</button> : null}
          </section>
        </main>
      ) : (
      <main className="lesson-v2">
        <header>
          <span>{lesson.moduleTitle}</span>
          {course.show_lesson_numbers !== false && (
            <span>
              LEKCJA {lesson.number_label || index + 1} / {lessons.length}
            </span>
          )}
        </header>
        <h1 data-testid="lesson-title">{lesson.title}</h1>
        <BrandedVideoPlayer
          lesson={lesson}
          courseCover={course.cover_url}
          startAt={savedPosition}
          onProgress={saveProgress}
        />
        <article className="lesson-content-v2" data-testid="lesson-content">
          <span>W TEJ LEKCJI</span>
          {lesson.content && <p>{lesson.content}</p>}
          <LessonBlockRenderer blocks={lesson.content_blocks || []} onButtonClick={trackButton} context={appearanceContext}/>
        </article>
        {visibleButtons.length > 0 && (
          <div className="lesson-ctas" data-testid="lesson-custom-buttons">
            {visibleButtons.map((button) => (
              <a
                className={`lesson-cta style-${button.style || "gold"} size-${button.size || "medium"}`}
                href={button.url}
                target={button.new_tab ? "_blank" : "_self"}
                rel="noreferrer"
                key={button.id}
                onClick={() => trackButton(button.id, button.label)}
                data-testid={`lesson-custom-button-${button.id}`}
              >
                <CourseCtaIcon name={button.icon || "external"}/>
                <span>{button.label}</span>
              </a>
            ))}
          </div>
        )}
        <LessonCustomForm form={lesson.custom_form} courseId={courseId} lessonId={lesson.id} key={`form-${lesson.id}`}/>
        {course.homework_enabled !== false && lesson.homework_enabled && (
          <section
            className="assignment-glass"
            data-testid="lesson-homework-form"
          >
            <div className="assignment-heading">
              <span>
                <ClipboardPen />
              </span>
              <div>
                <small>ZADANIE DO WYKONANIA</small>
                <h2>
                  {lesson.assignment_type === "quiz"
                    ? "Sprawdź swoją wiedzę"
                    : "Pokaż rezultat swojej pracy"}
                </h2>
              </div>
            </div>
            {lesson.assignment_type === "quiz" ? (
              (lesson.quiz_questions || []).map((question, qi) => (
                <article
                  className="quiz-question"
                  key={question.id}
                  data-testid={`quiz-question-${question.id}`}
                >
                  <strong>
                    {qi + 1}. {question.question}
                  </strong>
                  {question.options.map((option, oi) => (
                    <label key={oi}>
                      <input
                        type="radio"
                        name={question.id}
                        checked={answers[qi] === oi}
                        onChange={() =>
                          setAnswers((a) => {
                            const next = [...a];
                            next[qi] = oi;
                            return next;
                          })
                        }
                        data-testid={`quiz-answer-${question.id}-${oi}`}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </article>
              ))
            ) : lesson.assignment_type === "file" ? (
              <label className="student-file-upload">
                <FileUp />
                <span>
                  {fileId ? "Plik został dodany" : "Kliknij, aby dodać plik"}
                </span>
                <input
                  type="file"
                  onChange={upload}
                  data-testid="homework-file-input"
                />
              </label>
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  lesson.assignment_type === "link"
                    ? "Wklej link do swojej pracy…"
                    : "Wpisz swoją odpowiedź…"
                }
                data-testid="homework-content-input"
              />
            )}
            <button
              className="gold-button"
              onClick={submit}
              data-testid="submit-homework-button"
            >
              <Send />{" "}
              {lesson.assignment_type === "quiz"
                ? "SPRAWDŹ TEST"
                : "WYŚLIJ ZADANIE"}
            </button>
            {lesson.unlock_rule && lesson.unlock_rule !== "immediate" && (
              <p className="unlock-note">
                <LockKeyhole /> Następna lekcja otworzy się:{" "}
                {
                  {
                    lesson_complete: "po ukończeniu lekcji",
                    homework_submitted: "po wysłaniu zadania",
                    curator_approved: "po akceptacji kuratora",
                    test_passed: "po zdaniu testu",
                  }[lesson.unlock_rule]
                }
              </p>
            )}
          </section>
        )}
        <footer className="lesson-nav-v2 lesson-nav-auto">
          <button
            disabled={index === 0}
            onClick={() => choose(lessons[index - 1].id)}
            data-testid="previous-lesson-button"
          >
            <ChevronLeft /> POPRZEDNIA
          </button>
          {completed.includes(lesson.id) && (
            <span className="auto-complete-status done">
              <Check /> UKOŃCZONA
            </span>
          )}
          <button
            className={
              index < lessons.length - 1 &&
              (!completed.includes(lesson.id) ||
                locked.includes(lessons[index + 1]?.id))
                ? "next-lesson-locked"
                : ""
            }
            disabled={index === lessons.length - 1}
            data-locked={
              index < lessons.length - 1 &&
              (!completed.includes(lesson.id) ||
                locked.includes(lessons[index + 1]?.id))
            }
            onClick={() => {
              if (
                !completed.includes(lesson.id) ||
                locked.includes(lessons[index + 1]?.id)
              ) {
                toast.warning(
                  "Obejrzyj bieżącą lekcję do końca, aby odblokować następną.",
                );
                return;
              }
              trackButton("next_lesson", "Następna lekcja");
              choose(lessons[index + 1].id);
            }}
            data-testid="next-lesson-button"
          >
            NASTĘPNA <ChevronRight />
          </button>
        </footer>
        <button className="lesson-idea-button lesson-idea-button-prominent" onClick={() => { trackButton("lesson_idea", "Napisz nam pomysł"); setIdeaOpen(true); }} data-testid="lesson-idea-button"><Lightbulb/><span><strong>Masz pomysł, co możemy poprawić?</strong><small>Napisz nam — czytamy każdą sugestię.</small></span><ChevronRight/></button>
      </main>
      )}
      <StudentContactModal open={ideaOpen} onClose={() => setIdeaOpen(false)} type="idea" courseId={courseId}/>
    </div>
  );
}
